import React, { useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'
import { toast } from 'react-toastify'

import { Box, Button, ListItem, SidebarNoticeBox } from '@campaignhub/suit-theme'
import { useSetState } from '@campaignhub/react-hooks'

import PageContext from '@contexts/pageContext'
import useServiceJob from '@hooks/useServiceJob'
import useUploadResource from '@hooks/useUploadResource'
import useCurrentUser from '@hooks/useCurrentUser'
import useServiceJobSetAvailable from '@hooks/useServiceJobSetAvailable'

import userTypes from '@functions/userTypes'
import prestigeServices from '@functions/prestigeServices'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCloudDownload, faCloudUpload, faPaperPlane } from '@fortawesome/pro-light-svg-icons'

const assignServiceJobStatus = (serviceJobParam, createFn) => {
  createFn(serviceJobParam).then(({ errors, success }) => {
    if (!success && errors){
      toast.warning(errors[0])
    }
  })
}

const defaultState = {
  totalImageConfirmationDisplayed: false,
  confirmStatus: undefined,
}

const Footer = (props) => {
  const { callbacks: { toggleCheckActionStatus }, compressing, isMissingResources } = props
  const [state, setState] = useSetState(defaultState)
  const { totalImageConfirmationDisplayed } = state

  const { serviceJob } = useContext(PageContext)

  const useUploadResourcePayload = useUploadResource()
  const {
    callbacks: {
      launchResourceUploadModal,
    },
  } = useUploadResourcePayload

  const {
    callbacks: {
      updateServiceJobTotalAssets: updateFn,
      assignServiceJobStatus: createFn,
    },
    showDuskPositional,
    creating,
  } = useServiceJob({ id: serviceJob.id })

  const { callbacks: { getServiceJobSetAvailable }, serviceJobSetAvailable, loading } = useServiceJobSetAvailable(serviceJob.id)

  const { currentUser } = useCurrentUser()

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceGroups, serviceJobs, resourcesCompressRequests } = entities

  const duskJob = Object.values(serviceJobs).filter(x => ['Dusk Photography'].includes(x.service?.name) && x.campaign.id === serviceJob.campaign.id)[0]
  const filterCompressRequests = Object.values(resourcesCompressRequests).filter(x => x.serviceJobId === serviceJob.id)
  const latestZip = filterCompressRequests[Object.keys(filterCompressRequests)[Object.keys(filterCompressRequests).length - 1]]

  const updateTotalImages = (isDusk) => {
    swal.fire({
      title: isDusk ? 'Update Dusk Positional Images' : 'Update Total Images',
      html: '<div style="overflow-x: auto;">'
        + '<table style="width:100%;table-layout:fixed;">'
        + '<tr>'
        + '<td style="text-align:right;width:40%;">Total Images:</td>'
        + `<td style="width:50%;"><input type="number" id="totalAssets" class="swal2-input" style="width:90%;" value="${isDusk ? duskJob?.totalAssets : serviceJob.totalAssets}"></td>`
        + '<td style="text-align:left;width:10%;color:red;">*</td>'
        + '</tr>'
        + '<tr>'
        + '<td style="text-align:right;width:40%;position:relative;bottom:70px;">Note: </td>'
        + '<td style="width:50%;"><textarea id="assetNote" class="swal2-input" style="width:90%;height:150px;margin:0px;" ></textarea></td>'
        + '<td style="text-align:left;width:10%;color:red;position:relative;bottom:70px;">*</td>'
        + '</tr>'
        + '</table>'
        + '</div>',
      icon: 'info',
      showCancelButton: false,
      confirmButtonText: 'Confirm',
      showClass: 'slide-from-top',
      padding: '0.6rem',
      preConfirm: () => {
        const inputNumber = swal.getHtmlContainer().querySelector('#totalAssets').value
        const inputNote = swal.getHtmlContainer().querySelector('#assetNote').value
        if (!inputNote) swal.showValidationMessage('Note is required when updating Total Images.')
        if (!inputNumber) swal.showValidationMessage('Please input the total images.')

        const obj = { totalAssets: inputNumber, TotalAssetsNote: inputNote }
        updateFn(isDusk ? duskJob : serviceJob, obj).then(({ success }) => success)
      },
      allowOutsideClick: false,
    }).then((success) => {
      if (success){
        launchResourceUploadModal()
        toggleCheckActionStatus('upload-resources')
      } else swal.showValidationMessage('Unable to update total images.')
    })
  }

  const confirmTotalDuskImages = () => {
    setState({ totalImageConfirmationDisplayed: true })
    swal.fire({
      title: 'Confirm Dusk Positional Total',
      html: `This job requires ${duskJob?.totalAssets} dusk positional images. Please confirm if this is still correct.`,
      icon: 'question',
      showCancelButton: false,
      confirmButtonText: 'Yes',
      showDenyButton: true,
      denyButtonText: 'No',
      denyButtonColor: '#e2001a',
      showClass: 'slide-from-top',
      allowOutsideClick: false,
    }).then(({ value }) => {
      if (value){
        launchResourceUploadModal()
        toggleCheckActionStatus('upload-resources')
      } else updateTotalImages(true)
    })
}

  const confirmTotalImages = () => {
    setState({ totalImageConfirmationDisplayed: true })

    if (totalImageConfirmationDisplayed || !['Photography'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)){
      launchResourceUploadModal()
      toggleCheckActionStatus('upload-resources')
    } else {
      swal.fire({
        title: 'Confirm Total Images',
        html: `This job requires ${serviceJob.totalAssets} images.  Please confirm if this is still correct.`,
        icon: 'question',
        showCancelButton: false,
        confirmButtonText: 'Yes',
        showDenyButton: true,
        denyButtonText: 'No',
        denyButtonColor: '#e2001a',
        showClass: 'slide-from-top',
        allowOutsideClick: false,
      }).then(({ value }) => {
        if (value && showDuskPositional){
          confirmTotalDuskImages()
        } else if (value){
          launchResourceUploadModal()
          toggleCheckActionStatus('upload-resources')
        } else {
          updateTotalImages(false)
        }
      })
    }
  }

  const confirmResources = () => {
    swal.fire({
      title: 'Confirm Resources',
      html: 'Confirming will push the images to the customer. No further changes beyond this point.'
        + '<br/>Do you wish to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      if (value){
        const serviceJobParam = {
          id: serviceJob.id,
          serviceJobStatusId: 60,
          workflowComment: 'Confirm from CH-Resources',
        }
        assignServiceJobStatus(serviceJobParam, createFn)
        toggleCheckActionStatus('confirm-resources')
      }
    })
  }

  useEffect(() => {
    const interval = setInterval(() => {
      if (serviceJob.serviceJobStatusId === 60 || !prestigeServices.find(x => x.id === serviceJob.service?.id)){
        clearInterval(interval)
      } else { getServiceJobSetAvailable(serviceJob.id) }
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  return (
    <>
      {(currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id
      && prestigeServices.find(x => x.id === serviceJob.service?.id)
      && (!serviceJobSetAvailable?.canConfirm || isMissingResources || serviceJob.serviceJobStatusId === 60)) && (
        <ListItem
          boxProps={{
          borderTop: '1px solid',
          padding: 'large',
          lineHeight: '1.3',
          alignItems: 'center',
        }}
          disableHover
        >
          <SidebarNoticeBox
            boxProps={{
            marginBottom: 'medium',
            backgroundColor: 'lightcyan!important',
            borderColor: 'rgb(125, 208, 215, 0.5)!important',
          }}
          >
            <SidebarNoticeBox.Title>
              {serviceJob.serviceJobStatusId === 60 ? 'Awaiting Selection' : (isMissingResources ? 'Current quantity on the Gallery does not match with the client order '
              : 'Current resources does not match with Jobtrack')}
            </SidebarNoticeBox.Title>
            <SidebarNoticeBox.Paragraph>
              {serviceJob.serviceJobStatusId === 60 ? 'This job has already been confirmed.' : (isMissingResources ? 'Please review the client\'s order and try again.'
              : 'Please wait for the syncing to be completed and try again.')}
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        </ListItem>
      )}
      <ListItem
        boxProps={{
        borderTop: '1px solid',
        padding: 'large',
        lineHeight: '1.3',
        alignItems: 'center',
      }}
        flexDirection="row"
        style={{ cursor: 'pointer' }}
        disableHover
      >
        <Box alignItems="center" marginRight="auto" justifyContent="flexStart" width="auto">
          <Button
            size="medium"
            buttonStyle="secondary"
            icon={<FontAwesomeIcon icon={faCloudUpload} />}
            onClick={confirmTotalImages}
          >
            Upload Resources
          </Button>
        </Box>
        {(latestZip && latestZip.downloadUrl) && (
          <a style={{ textDecoration: 'none' }} href={latestZip.downloadUrl} download>
            <Box alignItems="center" marginLeft="auto" justifyContent="flexEnd" width="auto">
              <Button
                size="medium"
                buttonStyle="secondary"
                loading={compressing}
                disabled={!latestZip}
                icon={<FontAwesomeIcon icon={faCloudDownload} />}
              >
                Download ZIP ({latestZip.totalItems} items)
              </Button>
            </Box>
          </a>
        )}
        {(currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id && prestigeServices.find(x => x.id === serviceJob.service?.id)) && (
          <Box alignItems="center" marginLeft="medium" justifyContent="flexEnd" width="auto">
            <Button
              size="medium"
              buttonStyle="secondary"
              disabled={!serviceJobSetAvailable?.canConfirm || isMissingResources || serviceJob.serviceJobStatusId === 60}
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
              onClick={confirmResources}
              loading={loading || creating}
            >
              Confirm
            </Button>
          </Box>
        )}
      </ListItem>
    </>
  )
}

Footer.propTypes = {
  callbacks: PropTypes.object,
  compressing: PropTypes.bool,
  isMissingResources: PropTypes.bool,
}

export default Footer
